
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { NotificationFormData } from "@/types/notifications";
import { addAutoMonitoringForNotification } from "@/services/autoMonitoringService";


export const createNotificationInDb = async (
  notification: NotificationFormData
): Promise<any> => {
  console.log("Starting notification creation with form data:", notification);
  
  const hasVoNumber = notification.voNumber?.trim() !== '';
  const hasProducts = notification.selectedOrderProducts && notification.selectedOrderProducts.length > 0;

  // Validate: with VO number require products, without require message
  if (hasVoNumber && !hasProducts && !notification.message?.trim()) {
    throw new Error("Selecteer minimaal één product of vul een bericht in");
  }
  if (!hasVoNumber && !notification.message?.trim()) {
    throw new Error("Vul een bericht in");
  }

  // Generate summary message
  const finalMessage = hasProducts
    ? notification.selectedOrderProducts!.map(p => p.productTitle).join(', ')
    : notification.message;
  
  // Collect recipients
  const internalRecipients: string[] = [];
  if (notification.recipients.internal.group1) {
    internalRecipients.push("group1");
  }
  if (notification.recipients.internal.group2) {
    internalRecipients.push("group2");
  }
  
  console.log("Internal recipients:", internalRecipients);
  console.log("External recipients:", notification.recipients.external);
  
  // Ensure user ID is available
  if (!notification.userId) {
    throw new Error("User ID is required");
  }
  
  // Always ensure priority has a valid string value, never null
  const priorityValue = notification.priority === null ? "normaal" : notification.priority;
  
  try {
    // Insert the notification
    console.log("Inserting notification with data:", {
      type: notification.type,
      vo_number: notification.voNumber || null,
      exact_order_number: notification.exactOrderNumber || null,
      message: finalMessage,
      priority: priorityValue,
      status: notification.status,
      user_id: notification.userId,
      user_name: notification.userName || "Onbekende gebruiker"
    });
    
    const { data: notificationData, error: notificationError } = await supabase
      .from('notifications')
      .insert({
        type: notification.type,
        vo_number: notification.voNumber?.trim()
          ? (notification.voNumber.trim().replace(/\s+/g, '').toUpperCase().startsWith('VO')
            ? notification.voNumber.trim().replace(/\s+/g, '').toUpperCase()
            : `VO${notification.voNumber.trim().replace(/\s+/g, '').toUpperCase()}`)
          : null,
        exact_order_number: notification.exactOrderNumber || null,
        message: finalMessage,
        priority: priorityValue,
        status: notification.status,
        user_id: notification.userId,
        user_name: notification.userName || "Onbekende gebruiker"
      })
      .select()
      .single();
    
    if (notificationError) {
      console.error("Error inserting notification:", notificationError);
      throw notificationError;
    }
    
    console.log("Notification created successfully:", notificationData);
    
    // Post messages to notification_chat
    try {
      if (hasProducts) {
        if (notification.isOverarchingNotification) {
          const overarchingMessage = notification.selectedOrderProducts
            ?.find(p => p.productMessage?.trim())?.productMessage || finalMessage;
          await supabase.from('notification_chat').insert({
            notification_id: notificationData.id,
            user_id: notification.userId,
            user_name: notification.userName || "Onbekende gebruiker",
            message: overarchingMessage
          });
        } else {
          const chatMessages = notification.selectedOrderProducts!
            .filter(p => p.productMessage?.trim())
            .map(p => ({
              notification_id: notificationData.id,
              user_id: notification.userId,
              user_name: notification.userName || "Onbekende gebruiker",
              message: `${p.sku}: ${p.productMessage}`,
              attachment_url: p.imageUrl || null
            }));
          if (chatMessages.length > 0) {
            await supabase.from('notification_chat').insert(chatMessages);
          }
        }
      } else if (notification.message?.trim()) {
        await supabase.from('notification_chat').insert({
          notification_id: notificationData.id,
          user_id: notification.userId,
          user_name: notification.userName || "Onbekende gebruiker",
          message: notification.message
        });
      }
    } catch (chatError) {
      console.error("Error posting chat messages:", chatError);
    }
    
    
    // Insert selected order products into monitoring (only when products exist)
    if (hasProducts) {
      // Filter out products whose SKU is already referenced as base paint by another product
      const basePaintSkusSet = new Set(
        notification.selectedOrderProducts
          .filter(p => p.basePaintSku)
          .map(p => p.basePaintSku)
      );
      const filteredProducts = notification.selectedOrderProducts.filter(
        p => !basePaintSkusSet.has(p.sku)
      );

      const orderProductData = filteredProducts.map(product => ({
        notification_id: notificationData.id,
        product_name: product.productTitle,
        product_sku: product.sku,
        product_ean: product.ean || null,
        product_bc: product.artikelCode || "",
        product_quantity: product.quantity,
        product_color: product.color || null,
        product_eenheid: product.eenheid || null,
        is_monitored: true,
        match_notes: product.productMessage || null,
        base_paint_name: product.basePaintName || null,
        base_paint_sku: product.basePaintSku || null,
      } as any));
      
      console.log("Inserting selected order products:", orderProductData);
      
      const { error: orderProductError } = await supabase
        .from('notification_product_monitoring')
        .insert(orderProductData);
      
      if (orderProductError) {
        console.error("Error inserting order product data:", orderProductError);
        toast.error("Waarschuwing: Niet alle geselecteerde producten zijn opgeslagen");
      } else {
        console.log("Selected order products saved successfully");
      }
    }
    
    // Insert recipients if any
    const allRecipients = [...internalRecipients, ...notification.recipients.external];
    let recipientsSaved = false;
    
    if (allRecipients.length > 0) {
      const recipientsData = allRecipients.map(recipient => ({
        notification_id: notificationData.id,
        recipient_type: internalRecipients.includes(recipient) ? 'internal' : 'external',
        recipient_name: recipient
      }));
      
      console.log("Inserting recipients:", recipientsData);
      
      const { error: recipientsError } = await supabase
        .from('notification_recipients')
        .insert(recipientsData);
      
      if (recipientsError) {
        console.error("Error inserting recipients:", recipientsError);
        toast.error("Waarschuwing: Niet alle ontvangers zijn opgeslagen");
      } else {
        console.log("Recipients saved successfully");
        recipientsSaved = true;
      }
      
      // Send inkoop email for "Inkoop" (group2)
      if (recipientsSaved && notification.recipients.internal.group2) {
        console.log("Triggering inkoop email because Inkoop is selected:", {
          notification_id: notificationData.id
        });
        
        try {
          const emailProducts = notification.selectedOrderProducts?.map(p => ({
            product_name: p.productTitle,
            product_sku: p.sku,
            product_bc: p.artikelCode || "",
            product_quantity: p.quantity,
            product_color: p.color
              ? p.color.replace(/\s*\|\s*undefined\s*$/i, '').replace(/\s*\|\s*null\s*$/i, '').trim() || null
              : null,
            product_eenheid: p.eenheid || "1 ST",
            base_paint_name: p.basePaintName || null,
            base_paint_sku: p.basePaintSku || null,
          }));
          // Filter out products already referenced as base paint of another product
          const emailBasePaintSkus = new Set(
            emailProducts?.filter(p => p.base_paint_sku).map(p => p.base_paint_sku)
          );
          const filteredEmailProducts = emailProducts?.filter(
            p => !emailBasePaintSkus.has(p.product_sku)
          );
          const { data, error } = await supabase.functions.invoke('send-inkoop-email', {
            body: { notification_id: notificationData.id, products: filteredEmailProducts }
          });
          
          console.log("Inkoop email result:", data);
          
          if (error) {
            console.error("Inkoop email error:", error);
            toast.error("De melding is aangemaakt, maar de inkoop e-mail kon niet worden verzonden.");
          }
        } catch (inkoopError) {
          console.error("Error sending inkoop email:", inkoopError);
          toast.error("De melding is aangemaakt, maar de inkoop e-mail kon niet worden verzonden.");
        }
      }
    } else {
      console.log("No recipients to notify, skipping recipient saving");
    }
    
    // Auto-add monitoring for stock-relevant notifications without explicit products
    if (!hasProducts && finalMessage) {
      try {
        await addAutoMonitoringForNotification(notificationData.id, finalMessage, notification.status);
      } catch (monitoringError) {
        console.error("Error adding auto-monitoring:", monitoringError);
      }
    }
    
    // Send Telegram notification if status is "actie_decoprof"
    if (notification.status === "actie_decoprof") {
      console.log("Triggering Decoprof Telegram for actie_decoprof notification");
      try {
        const { data, error } = await supabase.functions.invoke('send-decoprof-telegram', {
          body: { notification_id: notificationData.id, old_status: null }
        });
        console.log("Decoprof Telegram result:", data);
        
        if (error) {
          console.error("Decoprof Telegram error:", error);
          toast.error("De melding is aangemaakt, maar het Telegram bericht kon niet worden verzonden.");
        }
      } catch (telegramError) {
        console.error("Error sending Decoprof Telegram:", telegramError);
        toast.error("De melding is aangemaakt, maar het Telegram bericht kon niet worden verzonden.");
      }
    }
    
    return notificationData;
  } catch (error) {
    console.error("Error in createNotificationInDb:", error);
    throw error;
  }
};
